import { logger } from '@adornis/base/logging.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { Observable } from 'rxjs';
import { classMetadataAgentModel, classMetadataAgentTemperature } from '../../../shared/models.js';
import { classMetadataAgentSystemPrompt } from '../../prompts.js';
import { getLLMRouter } from '../llm/llm-gate.js';

/**
 * Text-only classification agent that splits a free-text class/course label (e.g. "LK 12 Sozialkunde",
 * "8c Mathe") into grade/suffix/subject — see {@link classMetadataAgentSystemPrompt}. Called through
 * `runClassMetadataAgent` (extraction-modules/class-metadata.ts), which is what `linkExamToClassroom`
 * (db/utils/classroom.ts) uses to normalize every raw class label before it reaches the database.
 */
export const classMetadataAgent = registerSubscription({
  operationName: 'classMetadataAgent',
  type: () => String, // Output as JSON string to avoid _class error
  params: [{ name: 'raw', type: () => String }],
  resolve: (raw: string) => () =>
    new Observable<string>(subscriber => {
      void (async () => {
        try {
          const trimmed = typeof raw === 'string' ? raw.trim() : '';
          if (!trimmed) {
            subscriber.next(
              JSON.stringify({ answer: JSON.stringify({ grade: null, suffix: null, subject: null }), cost: 0 }),
            );
            return;
          }

          const router = getLLMRouter();
          const response = await router.chat.completions.create({
            model: classMetadataAgentModel,
            provider: 'openrouter',
            temperature: classMetadataAgentTemperature,
            messages: [
              { role: 'system', content: classMetadataAgentSystemPrompt },
              { role: 'user', content: trimmed },
            ],
            response_format: { type: 'json_object' },
          });

          const rawContent = response.choices?.[0]?.message?.content;
          const finalAnswer = typeof rawContent === 'string' ? rawContent : JSON.stringify(rawContent ?? '');
          const usageCost = Number((response.usage as any)?.cost ?? 0);
          logger.info({ usageCost, context: 'COST_TRACKING' }, 'Class Metadata Agent cost');

          subscriber.next(JSON.stringify({ answer: finalAnswer, cost: Number.isFinite(usageCost) ? usageCost : 0 }));
        } catch (e) {
          logger.error({ err: e, context: 'classMetadataAgent' }, 'Failed to extract class metadata');
          subscriber.error(
            JSON.stringify({
              error: e instanceof Error ? e.message : String(e),
              cost: 0,
            }),
          );
        } finally {
          subscriber.complete();
        }
      })();
    }),
});
